import React, { useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { Button } from '../ui/Kit';
import Spinner from '../ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { composeBodyToHtml, looksLikeHtml, sanitizeEmailHtml } from '../../lib/emailCompose';
import { validateScheduleValue } from '../../lib/scheduleSend';
import ScheduleSendControl, { GMAIL_SCHEDULE_NOTE } from './ScheduleSendControl';

const { FiX, FiEdit3, FiEye, FiSend, FiClock } = FiIcons;

/** Pasted rich content (text/html) → sanitized fragment markup for the editor. */
function clipboardHtmlToFragment(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('style, script, meta, link, title').forEach((el) => el.remove());
  const fragment = (doc.body?.innerHTML || '')
    .replace(/<!--\s*(Start|End)Fragment\s*-->/gi, '')
    .trim();
  return sanitizeEmailHtml(fragment);
}

export default function EmailComposeModal({
  isOpen,
  onClose,
  toEmail,
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  sending,
  gmailBusy,
  onSendBrevo,
  onSendGmail,
  onSchedule,
  scheduleMode,
  onScheduleModeChange,
  scheduleValue,
  onScheduleValueChange,
  notice,
}) {
  const [tab, setTab] = useState('write');
  const textareaRef = useRef(null);

  const html = useMemo(() => composeBodyToHtml(body), [body]);
  const canSend = !!subject.trim() && !!html && !sending && !gmailBusy;
  const isHtml = looksLikeHtml(body);
  const scheduling = scheduleMode === 'schedule';
  const scheduleCheck = scheduling ? validateScheduleValue(scheduleValue) : null;

  const handlePaste = (e) => {
    const pastedHtml = e.clipboardData?.getData('text/html');
    const pastedText = e.clipboardData?.getData('text/plain') || '';
    if (!pastedHtml || looksLikeHtml(pastedText)) return;
    const fragment = clipboardHtmlToFragment(pastedHtml);
    if (!fragment) return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    onBodyChange(body.slice(0, start) + fragment + body.slice(end));
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (node) node.selectionStart = node.selectionEnd = start + fragment.length;
    });
  };

  const tabClass = (key) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
      tab === key ? 'bg-skyTint text-navy' : 'text-gray-500 hover:text-navy hover:bg-ivory'
    }`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-4xl p-0 max-h-[92vh] flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-lineGray">
        <h2 className="text-base font-heading font-bold text-gray-900">Compose email</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-navy hover:bg-ivory"
          aria-label="Close"
        >
          <SafeIcon icon={FiX} />
        </button>
      </div>

      <div className="px-5 py-4 space-y-3 overflow-y-auto">
        <label className="flex items-center gap-3 text-sm">
          <span className="w-16 shrink-0 text-gray-500">To</span>
          <input
            type="email"
            value={toEmail}
            readOnly
            className="flex-1 border border-gray-200 rounded-md px-2.5 py-2 text-sm bg-ivory text-gray-700 cursor-default outline-none"
          />
        </label>
        <label className="flex items-center gap-3 text-sm">
          <span className="w-16 shrink-0 text-gray-500">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            disabled={sending}
            placeholder="Subject"
            className="flex-1 border border-gray-200 rounded-md px-2.5 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </label>

        <div>
          <div className="flex items-center gap-1 mb-2">
            <button type="button" className={tabClass('write')} onClick={() => setTab('write')}>
              <SafeIcon icon={FiEdit3} /> Write / HTML
            </button>
            <button type="button" className={tabClass('preview')} onClick={() => setTab('preview')}>
              <SafeIcon icon={FiEye} /> Preview
            </button>
            <span className="ml-auto text-[11px] text-gray-400">
              {isHtml ? 'HTML detected' : 'Plain text — line breaks are kept'}
            </span>
          </div>

          {tab === 'write' ? (
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => onBodyChange(e.target.value)}
              onPaste={handlePaste}
              disabled={sending}
              placeholder="Write your message, or paste HTML…"
              spellCheck={!isHtml}
              className={`w-full h-[48vh] min-h-[240px] border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-y ${
                isHtml ? 'font-mono text-xs' : ''
              }`}
            />
          ) : html ? (
            <iframe
              title="Email preview"
              sandbox=""
              referrerPolicy="no-referrer"
              srcDoc={html}
              className="w-full h-[48vh] min-h-[240px] border border-gray-200 rounded-md bg-white"
            />
          ) : (
            <div className="flex h-[48vh] min-h-[240px] items-center justify-center rounded-md border border-dashed border-gray-200 text-sm text-gray-400">
              Nothing to preview yet.
            </div>
          )}
        </div>

        {notice}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-3 border-t border-lineGray bg-ivory/60">
        <div className="space-y-1.5 min-w-0">
          <ScheduleSendControl
            mode={scheduleMode}
            onModeChange={onScheduleModeChange}
            value={scheduleValue}
            onValueChange={onScheduleValueChange}
            disabled={sending}
          />
          <p className="text-[11px] text-gray-500">
            {scheduling
              ? GMAIL_SCHEDULE_NOTE
              : 'Brevo sends are tracked (delivered, opened, clicked). Gmail opens are tracked approximately.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!canSend || scheduling}
            title={scheduling ? GMAIL_SCHEDULE_NOTE : undefined}
            onClick={() => onSendGmail({ html, subject: subject.trim() })}
          >
            {gmailBusy ? <><Spinner className="mr-2" />Opening…</> : 'Send with Gmail'}
          </Button>
          {scheduling ? (
            <Button
              size="sm"
              variant="primary"
              disabled={!canSend || !scheduleCheck?.iso}
              onClick={() => onSchedule({ html, subject: subject.trim(), sendAt: scheduleCheck.iso })}
            >
              {sending ? <><Spinner className="mr-2" />Scheduling…</> : <><SafeIcon icon={FiClock} className="mr-1.5" />Schedule send</>}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              disabled={!canSend}
              onClick={() => onSendBrevo({ html, subject: subject.trim() })}
            >
              {sending ? <><Spinner className="mr-2" />Sending…</> : <><SafeIcon icon={FiSend} className="mr-1.5" />Send Email</>}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
